import React, { useState } from 'react';
import {
  Box,
  Avatar,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Stack,
  Alert,
  Fab,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Scale as ScaleIcon,
  List as ListIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { birdWeightsService, birdsService } from '../services/apiServices';
import BirdWeightDialog from '../components/BirdWeights/BirdWeightDialog';
import dayjs from 'dayjs';

const BirdWeights: React.FC = () => {
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  const [filterBirdId, setFilterBirdId] = useState<string>('');
  const [filterWeek, setFilterWeek] = useState<string>('');

  // Fetch birds for filter dropdown
  const { data: birdsData } = useQuery({
    queryKey: ['birds'],
    queryFn: () => birdsService.getAll(),
  });

  // Fetch bird weights with filters
  const {
    data: weightsData,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: ['birdWeights', filterBirdId, filterWeek],
    queryFn: () => {
      const params: any = {};
      if (filterBirdId) params.bird_id = filterBirdId;
      if (filterWeek) params.week_number = filterWeek;
      return birdWeightsService.getAll(params);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: birdWeightsService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['birdWeights'] });
    },
  });

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this weight record?')) {
      deleteMutation.mutate(id);
    }
  };

  const birds = birdsData?.data || [];
  const weights = weightsData?.data || [];
  const isLoadingWeights = isLoading || isFetching || deleteMutation.isPending;
  return (
    <Box>
      {isLoadingWeights && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800 0%, #ffa726 100%)',
            },
          }}
        />
      )}
      <Fab
        color="primary"
        aria-label="add"
        onClick={() => setOpenDialog(true)}
        sx={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          background: 'linear-gradient(135deg, #ff9800 0%, #ffa726 100%)',
          color: '#ffffff',
          boxShadow: '0px 6px 20px rgba(255, 152, 0, 0.4)',
          '&:hover': {
            background: 'linear-gradient(135deg, #f57c00 0%, #ff9800 100%)',
            boxShadow: '0px 8px 24px rgba(255, 152, 0, 0.5)',
          },
        }}
      >
        <AddIcon />
      </Fab>

      {/* Page Header Banner */}
      <Box
        sx={{
          mb: 3.5,
          p: 3,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #161616 0%, #1f1f1f 100%)',
          border: '1px solid rgba(255,152,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Avatar sx={{ bgcolor: 'rgba(33,150,243,0.15)', color: '#2196f3', width: 52, height: 52 }}>
          <ScaleIcon fontSize="medium" />
        </Avatar>
        <Box flex={1}>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ color: '#ffffff', letterSpacing: '-0.01em' }}
          >
            Bird Weights
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', mt: 0.25 }}>
            Track and monitor weekly weight measurements for your flock
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Filter by Bird"
              select
              value={filterBirdId}
              onChange={(e) => setFilterBirdId(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All Birds</MenuItem>
              {birds.map((bird: any) => (
                <MenuItem key={bird.id} value={bird.id}>
                  {bird.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Filter by Week"
              type="number"
              value={filterWeek}
              onChange={(e) => setFilterWeek(e.target.value)}
              sx={{ minWidth: 150 }}
              placeholder="Week number"
              slotProps={{ htmlInput: { min: 1 } }}
            />

            {(filterBirdId || filterWeek) && (
              <Button
                variant="outlined"
                onClick={() => {
                  setFilterBirdId('');
                  setFilterWeek('');
                }}
              >
                Clear Filters
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error">Failed to load bird weights. Please try again.</Alert>}

      {/* Table */}
      {!isLoading && !error && (
        <Paper
          sx={{
            p: 0,
            border: '1px solid rgba(255,152,0,0.2)',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {/* Table Section Header */}
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
              borderBottom: '1px solid rgba(255,152,0,0.15)',
            }}
          >
            <ListIcon sx={{ color: '#ff9800', fontSize: '1.1rem' }} />
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#ffffff' }}>
              Weight Records
            </Typography>
            <Chip
              label={weights.length}
              size="small"
              sx={{
                bgcolor: 'rgba(255,152,0,0.12)',
                color: '#ff9800',
                fontWeight: 700,
                height: 20,
              }}
            />
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Bird</TableCell>
                  <TableCell>Week #</TableCell>
                  <TableCell>Avg Weight (kg)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Sample Size</TableCell>
                  <TableCell>Weigh Date</TableCell>
                  <TableCell>Recorded By</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {weights.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        No weight records found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  weights.map((weight: any) => {
                    const getStatusColor = (status: string) => {
                      switch (status) {
                        case 'severely_underweight':
                          return 'error';
                        case 'underweight':
                          return 'warning';
                        case 'standard':
                          return 'success';
                        case 'slightly_overweight':
                          return 'info';
                        case 'overweight':
                          return 'warning';
                        default:
                          return 'default';
                      }
                    };

                    const getStatusLabel = (status: string) => {
                      switch (status) {
                        case 'severely_underweight':
                          return 'Severely Underweight';
                        case 'underweight':
                          return 'Underweight';
                        case 'standard':
                          return 'Standard';
                        case 'slightly_overweight':
                          return 'Slightly Over';
                        case 'overweight':
                          return 'Overweight';
                        default:
                          return 'Unknown';
                      }
                    };

                    return (
                      <TableRow key={weight.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {weight.bird?.name || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {weight.bird?.birds_type || ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={`Week ${weight.week_number}`} size="small" color="primary" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {weight.average_weight_kg} kg
                          </Typography>
                          {weight.standard_weight && (
                            <Typography variant="caption" color="text.secondary">
                              Expected: {weight.standard_weight} kg
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {weight.weight_status ? (
                            <Chip
                              label={getStatusLabel(weight.weight_status)}
                              size="small"
                              color={getStatusColor(weight.weight_status) as any}
                              title={weight.weight_status.message}
                            />
                          ) : (
                            <Chip label="N/A" size="small" />
                          )}
                        </TableCell>
                        <TableCell>{weight.sample_size} birds</TableCell>
                        <TableCell>{dayjs(weight.weigh_date).format('MMM D, YYYY')}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {weight.recorder?.name || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                            sx={{ maxWidth: 200 }}
                          >
                            {weight.notes || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(weight.id)}
                            disabled={deleteMutation.isPending}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Dialog */}
      <BirdWeightDialog open={openDialog} onClose={() => setOpenDialog(false)} birds={birds} />
    </Box>
  );
};

export default BirdWeights;
